import { useCallback, useEffect, useState } from 'react';
import { axiosInstances } from '../utils/axios';
import { useAuth0, useAuth0Data } from 'neon-auth0';
import { MFA_REQUIRED } from '../constants/request';
import { useMfa } from '../providers/MfaProvider';
import { askUserToMfa, isMfaEnabled, isMfaTokenValid } from '../components/Auth/MfaConfig';
import { logEvent } from '../utils/logger';

export const useAxiosMiddleware = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { accessToken } = useAuth0Data();
  const { isMfa, setIsMfa, scope } = useMfa();
  const [isReady, setIsReady] = useState(false);

  const processMfaError = useCallback(() => {
    if (!isMfa) {
      return setIsMfa(true);
    }

    const requiresHardReload = isMfa || !isMfaTokenValid(accessToken);
    if (requiresHardReload) {
      return askUserToMfa();
    }
    return setIsMfa(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMfa, setIsMfa, scope]);

  useEffect(() => {
    const interceptors: any = [];
    axiosInstances.forEach((axiosInstance) => {
      const requestInterceptor = axiosInstance.interceptors.request.use(async (config) => {
        config.headers.common = {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
          'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
          'X-Content-Type-Options': 'nosniff',
          'Content-Security-Policy': "frame-ancestors 'none'",
          'X-Frame-Options': 'DENY',
          'Referrer-Policy': 'no-referrer',
          'Permissions-Policy': 'geolocation=(), camera=(), microphone=()'
        };

        return config;
      });

      const responseInterceptor = axiosInstance.interceptors.response.use(
        (res) => res,
        (err) => {
          const { response } = err || {};
          const { data } = response || {};

          if (data?.title === MFA_REQUIRED && isMfaEnabled) {
            processMfaError();
          }

          logEvent({
            event: 'api_request_failed',
            level: 'Warning',
            url: err.config?.url,
            method: err.config?.method,
            errorMessage: err.message,
            statusCode: response?.status
          });

          return Promise.reject(err);
        }
      );

      interceptors.push({
        axiosInstance,
        requestInterceptor,
        responseInterceptor
      });
    });

    setIsReady(true);
    return () => {
      interceptors.forEach((obj: any) => {
        obj.axiosInstance.interceptors.request.eject(obj.requestInterceptor);
        obj.axiosInstance.interceptors.response.eject(obj.responseInterceptor);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently]);

  return { isReady };
};
